// src/Pages/Yhteistyo/Yhteistyo.jsx
import React, { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "@dr.pogodin/react-helmet";
import { motion } from "framer-motion";
import WebDesignBanner from "../../components/HeaderWithAd/HeaderWithAd";
import NeonButton from "../../components/ResponsiveButton/NeonButton.jsx";
import yhteistyoHero from "../../assets/yhteistyo-dark.webp";

// Pieni apuri: animoidut tekstirivit hero-otsikkoon
const AnimatedLine = ({ children, delay = 0 }) => {
  return (
    <motion.span
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, ease: "easeOut", delay }}
      className="block"
    >
      {children}
    </motion.span>
  );
};

export default function YhteistyoPage() {
  const rrLocation = useLocation();
  const navigate = useNavigate();

  // SEO + canonical: johdetaan automaattisesti polusta
  const seo = useMemo(() => {
    const title =
      "Yhteistyö Buukkerin kanssa – esiintyjät, tapahtumat ja livemusiikki | Buukkeri";
    const description =
      "Buukkeri on suomalainen esiintyjäalusta, joka helpottaa livemusiikin ja esiintyjien buukkausta ravintoloille, tapahtumajärjestäjille, yrityksille ja kumppaneille.";
    const origin =
      typeof window !== "undefined"
        ? window.location.origin
        : "https://buukkeri.fi";

    // Varmistetaan, että canonical päättyy aina vinoviivaan
    const path = rrLocation?.pathname || "/yhteistyo/";
    const cleanPath = path.endsWith("/") ? path : `${path}/`;
    const canonical = `${origin}${cleanPath}`;

    return { title, description, canonical };
  }, [rrLocation?.pathname]);

  // CTA-napit herossa: sähköposti + siirto etusivun buukkauskohtaan
  const handleContactClick = () => {
    window.location.href =
      "mailto:info@buukkeri.fi?subject=Yhteistyö%20Buukkerin%20kanssa";
  };

  const handleBookingClick = () => {
    // Ohjaa etusivulle ja ankkuriin (päivitä polku jos käytössä jokin muu lomake)
    navigate("/#booking");
  };

  return (
    <>
      {/* META / SEO */}
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <link rel="canonical" href={seo.canonical} />

        {/* OG / Twitter */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Buukkeri" />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:url" content={seo.canonical} />
        <meta property="og:locale" content="fi_FI" />
        <meta
          property="og:image"
          content="https://buukkeri.fi/wp-content/uploads/2025/10/og-default-1200x630-1.jpg"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seo.title} />
        <meta name="twitter:description" content={seo.description} />
        <meta
          name="twitter:image"
          content="https://buukkeri.fi/wp-content/uploads/2025/10/og-default-1200x630-1.jpg"
        />

        {/* JSON-LD: WebPage */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: seo.title,
            description: seo.description,
            url: seo.canonical,
          })}
        </script>

        {/* JSON-LD: Organization (sama kuin muualla sivustolla) */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Buukkeri",
            url: "https://buukkeri.fi/",
            logo: "https://buukkeri.fi/wp-content/uploads/2025/10/og-default-1200x630-1.jpg",
          })}
        </script>
      </Helmet>

      {/* HERO – oma taustakuva vain yhteistyösivulle */}
      <WebDesignBanner bgImage={yhteistyoHero} tall hideLogoMobile>
        {/* Hero-otsikko taustakuvan päällä */}
        <div className="text-center max-w-3xl mx-auto">
          <h1 className="mb-6 dark:text-white/90 text-white/90 font-customFont1 text-base sm:text-lg md:text-2xl font-semibold text-center text-shadow-lg leading-relaxed">
            <AnimatedLine type="fade">
              Etsitkö kumppania livemusiikin ja esiintyjien buukkaukseen?
            </AnimatedLine>
            <AnimatedLine delay={0.18}>
              Buukkeri helpottaa ravintoloiden, tapahtumien ja yritysjuhlien
              esiintyjäbuukkauksia – ilman sähköpostirallia ja
              hintayllätyksiä.
            </AnimatedLine>
          </h1>
        </div>

        {/* Hero-kortti – “samanlainen laatikko” kuin etusivun hakulomake */}
        <div className="relative z-30 overflow-visible mx-auto w-full max-w-5xl rounded-2xl p-6 sm:p-8 bg-lemon/70 dark:bg-black/40 backdrop-blur-md shadow-lg ring-1 ring-white/40">
          <div className="space-y-4 text-left text-black/85 dark:text-white/85 font-customFont1 text-xs md:text-lg">
            <p>
              Buukkeri on suomalainen esiintyjäalusta, joka yhdistää
              bilebändit, duot, DJ:t ja trubaduurit niihin iltoihin, joissa
              livemusiikki tekee tapahtumasta aidosti muistettavan.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              <div>
                <h2 className="font-semibold text-base sm:text-lg mb-2">
                  Mitä Buukkeri tuo pöytään?
                </h2>
                <ul className="list-disc pl-4 space-y-1 text-xs md:text-lg">
                  <li>Reaaliaikainen esiintyjien saatavuus.</li>
                  <li>
                    Selkeä kokonaishinnoittelu – esiintyminen ja matkakulut
                    samassa paketissa.
                  </li>
                  <li>
                    Vähemmän manuaalista säätöä – vähemmän puheluita ja
                    loputonta sähköpostirallia.
                  </li>
                  <li>
                    Palaute ja arviot, jotka helpottavat seuraavien iltojen
                    suunnittelua.
                  </li>
                  <li>
                    Toimii valtakunnallisesti – yksi työkalu koko Suomeen.
                  </li>
                  <li>
                    Helposti laajennettavissa – Buukkeriin voidaan lisätä uusia
                    esiintyjätyyppejä helposti.
                  </li>
                </ul>

                {/* Linkit valtakunnallisiin hintasivuihin */}
                <p className="mt-3 text-xs md:text-sm text-black/80 dark:text-white/80">
                  Katso valtakunnalliset hintasivut, joilla näkyvät
                  suosituimmat setit ja keskihinnat koko Suomessa:{" "}
                  <a
                    href="https://buukkeri.fi/bilebandi-juhliin/"
                    className="underline font-semibold hover:text-pink"
                  >
                    Bilebändi juhliin – hinnat ja suosituimmat setit
                  </a>{" "}
                  ja{" "}
                  <a
                    href="https://buukkeri.fi/trubaduuri-juhliin/"
                    className="underline font-semibold hover:text-pink"
                  >
                    Trubaduuri juhliin – hinnat ja suosituimmat setit
                  </a>
                  .
                </p>
              </div>

              <div>
                <h2 className="font-semibold text-xs md:text-lg mb-2">
                  Kenelle yhteistyö sopii?
                </h2>
                <ul className="list-disc pl-4 space-y-1 text-xs md:text-lg">
                  <li>
                    Live-musiikkia tai esiintyjiä hyödyntävät ravintolat,
                    baarit ja yökerhot.
                  </li>
                  <li>
                    Yritykset, joilla on henkilöstöjuhlia, asiakastilaisuuksia,
                    messuja tai muita tapahtumia.
                  </li>
                  <li>
                    Yksityishenkilöt, jotka järjestävät häitä, synttäreitä,
                    pikkujouluja tai muita juhlia ja kaipaavat luotettavaa
                    esiintyjäkumppania.
                  </li>
                  <li>
                    Tapahtumajärjestäjät ja tuottajat, joilla on useampia
                    tapahtumia vuoden aikana.
                  </li>
                  <li>
                    Ohjelmatoimistot, äänentoisto- ja
                    tapahtumatekniikkayritykset sekä muut
                    tapahtumapalveluiden tarjoajat.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </WebDesignBanner>

      {/* VARSINAINEN SIVUSISÄLTÖ HERON ALLA */}
      <main className="relative min-h-screen mt-8 w-full pb-12">
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Miksi juuri Buukkeri */}
          <section className="py-6 space-y-3">
            <h2 className="font-customFont2 text-2xl sm:text-3xl text-black/90 dark:text-white/90 mb-2">
              Miksi juuri Buukkeri?
            </h2>

            <p className="font-customFont1 text-black/80 dark:text-white/80 text-xs md:text-lg">
              Buukkeri syntyi käytännön tarpeesta: yhdestä bändistä, yhdestä
              buukkauslomakkeesta ja keikkabuukkarista, joka halusi lopettaa
              exceleiden ja loputtomien sähköpostiketjujen kanssa
              painimisen.
            </p>

            <p className="font-customFont1 text-black/80 dark:text-white/80 text-xs md:text-lg">
              Aluksi Buukkerin logiikka koodattiin{" "}
              <a
                href="https://buukkeri.fi/esiintyja/bilebandi-joyride/"
                target="_blank"
                rel="noopener noreferrer"
                className="underline font-semibold hover:text-pink"
              >
                Bilebändi Joyriden sivuille
              </a>{" "}
              automaattiseksi tarjouslomakkeeksi, joka laski hinnan,
              tarkisti onko keikkapäivä vapaa ja muodosti tarjouksen. Kun
              ratkaisu toimi, samaa logiikkaa kehitettiin pidemmälle
              sisäisenä työkaluna – oikeilla keikoilla, hinnoilla ja
              keikkailualueilla testaten.
            </p>

            <p className="font-customFont1 text-black/80 dark:text-white/80 text-xs md:text-lg">
              Lopulta oli selvää, ettei kyse ollut enää vain yhden bändin
              apuvälineestä, vaan tarvittiin varsinainen buukkausalusta
              esiintyjille ja tilaajille. Siksi Buukkeri on rakennettu
              käytännön keikkatilanteita varten – ei vain paperilla hyvältä
              näyttäväksi ratkaisuksi.
            </p>

            <ul className="font-customFont1 text-black/85 dark:text-white/85 text-xs md:text-lg list-disc pl-5 space-y-1 mt-2">
              <li>
                <strong>Käytännössä testattu</strong> – käytössä oikeilla
                keikoilla ja oikeilla asiakkailla, ei pelkkä paperilla
                hyvältä näyttävä konsepti.
              </li>
              <li>
                <strong>Suomalainen palvelu</strong> – tunnemme paikallisen
                keikka- ja ravintolakulttuurin sekä sesongit.
              </li>
              <li>
                <strong>Jatkuvasti kehittyvä</strong> – ominaisuuksia
                kehitetään eteenpäin esiintyjiltä, asiakkailta ja
                kumppaneilta saadun palautteen pohjalta.
              </li>
            </ul>
          </section>

          {/* FAQ / CTA */}
          <section className="py-8 border-t border-black/10 dark:border-white/10 mt-4">
            <h2 className="font-customFont2 text-2xl sm:text-3xl text-black/90 dark:text-white/90 mb-4">
              Kiinnostuitko yhteistyöstä?
            </h2>
            <p className="font-customFont1 text-black/80 dark:text-white/80 text-xs md:text-lg mb-4"></p>

            {/* NAPIT: mobiilissa koko leveys, desktopilla vierekkäin */}
            <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:flex-wrap">
              <NeonButton
                variant="primary"
                size="md"
                onClick={handleContactClick}
                className="w-full justify-center sm:w-auto"
              >
                Lähetä sähköposti
              </NeonButton>

              <NeonButton
                variant="secondary"
                size="md"
                onClick={handleBookingClick}
                className="w-full justify-center sm:w-auto"
              >
                Katso vapaat esiintyjät
              </NeonButton>
            </div>
          </section>
        </div>
      </main>
    </>
  );
}
